/**
 * Custom hook for Audit Logs data fetching and management
 */

import { useState, useCallback } from "react";
import { PaginatedAuditLogs, AuditLogFilters } from "./types";
import auditLogsApiService from "./auditLogsApiService";
import { AUDIT_LOGS_API_CONFIG } from "./apiConfig";

export const useAuditLogs = (
  initialFilters: AuditLogFilters = {
    page: AUDIT_LOGS_API_CONFIG.PAGINATION.DEFAULT_PAGE,
    per_page: AUDIT_LOGS_API_CONFIG.PAGINATION.DEFAULT_PER_PAGE,
    time_range: "last_24_hours",
  }
) => {
  const [data, setData] = useState<PaginatedAuditLogs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>(initialFilters);

  const fetchAuditLogs = useCallback(async (newFilters?: AuditLogFilters) => {
    setLoading(true);
    setError(null);
    try {
      const filtersToUse = newFilters || filters;
      const response = await auditLogsApiService.getAuditLogs(filtersToUse);
      setData(response);
      setFilters(filtersToUse);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch audit logs";
      setError(errorMessage);
      console.error("Error fetching audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const updateFilters = useCallback(
    (newFilters: Partial<AuditLogFilters>) => {
      const updatedFilters = { ...filters, ...newFilters, page: 1 };
      setFilters(updatedFilters);
      return updatedFilters;
    },
    [filters]
  );

  const changePage = useCallback(
    (page: number) => {
      const updatedFilters = { ...filters, page };
      setFilters(updatedFilters);
      return updatedFilters;
    },
    [filters]
  );

  return {
    data,
    loading,
    error,
    filters,
    fetchAuditLogs,
    updateFilters,
    changePage,
  };
};

export default useAuditLogs;
